import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBuilding } from '@fortawesome/free-solid-svg-icons'
import { getProfilePicUrl } from '../../utils/personUtils'
import { formatDuration } from '../../utils/dateUtils'
import { MapExtra } from '../../utils/MapExtra'
import { PersonLiteDto } from '../../api/person/models/PersonDto'
import { CompanyDto } from '../../api/company/models/CompanyDto'
import { TransformedResolvedPerson } from './HelixCandidateResultList'

type Props = {
    transformedResolvedPerson: TransformedResolvedPerson
    networkLinksPersonLiteCache: MapExtra<string, PersonLiteDto>
    companyCache: MapExtra<string, CompanyDto>
}

const HelixCandidateConnectionDetails: React.FC<Props> = ({
    transformedResolvedPerson,
    networkLinksPersonLiteCache,
    companyCache,
}) => {
    const [expandedCompanyIds, setExpandedCompanyIds] = useState<Set<string>>(
        new Set(),
    )

    const toggleCompany = (companyId: string) => {
        setExpandedCompanyIds((prev) => {
            const next = new Set(prev)
            if (next.has(companyId)) {
                next.delete(companyId)
            } else {
                next.add(companyId)
            }
            return next
        })
    }

    return (
        <div>
            {transformedResolvedPerson.companies.map((company, index) => {
                const isExpanded = expandedCompanyIds.has(company.companyId)
                const visibleConnections = isExpanded
                    ? company.connections
                    : company.connections.slice(0, 3)

                const isFirst = index === 0
                const isLast =
                    index === transformedResolvedPerson.companies.length - 1
                const className = `${!isLast ? 'border-b' : ''} pb-2 ${
                    !isFirst ? 'pt-2' : ''
                }`.trim()
                const companyDto = companyCache.getOrThrow(company.companyId)

                return (
                    <div key={company.companyId} className={className}>
                        <div className="flex items-center gap-2 mb-2 ml-1">
                            <FontAwesomeIcon
                                icon={faBuilding}
                                className="text-ctxt-blue"
                            />
                            <h3 className="text-base font-semibold">
                                {companyDto.name}
                            </h3>
                        </div>

                        <div className="text-sm text-gray-800">
                            {visibleConnections.map((connection, idx) => {
                                const seedPerson =
                                    networkLinksPersonLiteCache.getOrThrow(
                                        connection.seedPersonId,
                                    )
                                const seedTitles =
                                    connection.seedPersonJobTitles.join(' | ')
                                const duration = formatDuration(
                                    connection.overallStart,
                                    connection.overallEnd,
                                ).duration

                                return (
                                    <div
                                        key={idx}
                                        className="flex items-start gap-2"
                                    >
                                        <img
                                            src={getProfilePicUrl(
                                                seedPerson,
                                                true,
                                            )}
                                            className="rounded-full w-6 h-6 mt-0.5"
                                            onError={(e) => {
                                                e.currentTarget.onerror = null
                                                e.currentTarget.src =
                                                    '/no-profile-pic.png'
                                            }}
                                        />
                                        <div className="mt-1">
                                            <span className="font-medium">
                                                {seedPerson.firstName}{' '}
                                                {seedPerson.lastName}
                                            </span>
                                            {' | '}
                                            {seedTitles}
                                            {connection.overallStart && (
                                                <span className="text-gray-500 italic">
                                                    {` · ${new Date(
                                                        connection.overallStart,
                                                    ).getFullYear()} - ${
                                                        connection.overallEnd
                                                            ? new Date(
                                                                  connection.overallEnd,
                                                              ).getFullYear()
                                                            : 'Present'
                                                    } · ${duration}`}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}

                            {company.connections.length > 3 && (
                                <button
                                    className="text-xs text-blue-600 mt-1 hover:underline"
                                    onClick={() =>
                                        toggleCompany(company.companyId)
                                    }
                                >
                                    {isExpanded
                                        ? 'Show less'
                                        : `Show all (${company.connections.length})`}
                                </button>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export default HelixCandidateConnectionDetails
